import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";

import ACTION from "../../store/action";
import { actions as CommonActions } from "@tailormed/common-client/store/action";
import { selectFiltersModified } from "../../store/selector";
import { selectedViews } from "../../constant/table";
import SuccessMessage from "../../component/customComponent/customMessages/SuccessMessage";
import ErrorMessage from "../../component/customComponent/customMessages/ErrorMessage";
import { useInlineEditTracking } from "../../component/TableSection/InlineEdit/useInlineEditTracking";
import { InlineEditStatus } from "../../utils/pendo/useStandardizedTracking";
import {
  updateFollowupDateAPI,
  updatePatientAssigneesAPI,
  updatePatientDeceasedAPI,
  updatePatientFacility,
  updatePatientPhoneAPI,
  updatePatientPhysician,
  updatePatientStatusAPI
} from "../../api/api";

const buildApiCallAndPatch = (record, columnKey, newValue, lookups = {}) => {
  const patientId = record.patientId;
  const { assignees = [], facilities = [], physicians = [] } = lookups;
  switch (columnKey) {
    case "patientStatus":
      return {
        apiCall: () => updatePatientStatusAPI(patientId, newValue),
        patch: { status: newValue }
      };
    case "assignee": {
      const idArray = Array.isArray(newValue) ? newValue.map((v) => String(v)) : newValue ? [String(newValue)] : [];
      const lookupById = new Map(assignees.map((a) => [String(a.id), a]));
      const apiPayload = idArray.map((id) => {
        const a = lookupById.get(id);
        return { id, type: a?.type || "user" };
      });
      const enrichedAssignees = idArray.map((id) => {
        const a = lookupById.get(id);
        const displayName = a?.displayName || "";
        return {
          assignee: id,
          userName: displayName,
          assigneeName: displayName,
          assigneeType: a?.assigneeType
        };
      });
      return {
        apiCall: () => updatePatientAssigneesAPI(patientId, apiPayload),
        patch: { assignees: enrichedAssignees }
      };
    }
    case "facility": {
      const idArray = Array.isArray(newValue) ? newValue : newValue != null ? [newValue] : [];
      const lookupById = new Map(facilities.map((f) => [f.id, f]));
      const enrichedFacilities = idArray.map((id) => {
        const f = lookupById.get(id);
        return { id, name: f?.name || "" };
      });
      return {
        apiCall: () => updatePatientFacility(patientId, idArray),
        patch: { facilities: enrichedFacilities }
      };
    }
    case "physician": {
      const selected = physicians.find((p) => p.id === newValue);
      // Prefer `name` (single field shipped by Provider API); legacy first/last fallback last.
      const fullName = selected
        ? selected.name || [selected.firstName, selected.lastName].filter(Boolean).join(" ") || null
        : null;
      return {
        apiCall: () => updatePatientPhysician(patientId, newValue || null),
        patch: {
          physicianId: newValue || null,
          physician_full_name: fullName
        }
      };
    }
    case "phone_number": {
      // Empty → null so the backend clears the field instead of rejecting "" as invalid.
      const phone = newValue ? newValue : null;
      // Edit the field that's actually displayed: the column shows phone_number, falling back to
      // home_number (see PatientPhoneNumberColumn). Target that same field so clearing/editing the
      // shown number updates the right one instead of always hitting the mobile.
      const phoneField = record.phone_number ? "phone_number" : record.home_number ? "home_number" : "phone_number";
      return {
        apiCall: () => updatePatientPhoneAPI(patientId, phone, phoneField),
        patch: { [phoneField]: phone }
      };
    }
    case "followupDate": {
      const iso = newValue && moment.isMoment(newValue) ? newValue.toISOString() : newValue;
      return {
        apiCall: () => updateFollowupDateAPI(patientId, iso),
        patch: { followupDate: iso }
      };
    }
    case "isDeceased":
      return {
        apiCall: () => updatePatientDeceasedAPI(patientId, Boolean(newValue)),
        patch: { isDeceased: Boolean(newValue) }
      };
    default:
      return null;
  }
};

// `options.view` + `options.actionCreator` route the optimistic Redux patch into the
// caller's table slice (patient endpoints stay the same regardless of view).
const useInlineEditPatient = (lookups = {}, options = {}) => {
  const { view = selectedViews.allPatients, actionCreator = ACTION.updatePatientsInPlace } = options;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const filtersModified = useSelector(selectFiltersModified);
  const { trackInlineEditTriggered, trackInlineEditCompleted } = useInlineEditTracking();

  const commit = useCallback(
    async (record, columnKey, newValue) => {
      const plan = buildApiCallAndPatch(record, columnKey, newValue, lookups);
      if (!plan) {
        return { ok: false };
      }
      trackInlineEditTriggered({ attribute: columnKey });
      const startedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
      // Fan-out actions key by patientId and merge — send a slim patch so we
      // don't overwrite sibling rows' applicationId/claimId.
      const isPatientFanOut =
        actionCreator === ACTION.updateEnrollmentsForPatientInPlace ||
        actionCreator === ACTION.updateClaimsForPatientInPlace;
      const payload = isPatientFanOut
        ? [{ patientId: record.patientId, ...plan.patch }]
        : [{ ...record, ...plan.patch }];

      // Optimistic: reflect the change in the column immediately so the user doesn't wait
      // for the network round-trip. Reverted below if the API call fails.
      dispatch(actionCreator(view, payload));
      if (record.patientId) {
        dispatch(CommonActions.updateRecentPatient(record.patientId, plan.patch));
      }

      try {
        await plan.apiCall();
        // Active filters: the edited row may no longer match, so re-run the search (backend
        // applies filters). Skipped for patient-journey fan-out views and when no filter is set.
        if (filtersModified && !isPatientFanOut) {
          dispatch(ACTION.setTableDataAction(null, view));
        }
        SuccessMessage(t("inline_edit.success_toast"));
        trackInlineEditCompleted({
          attribute: columnKey,
          status: InlineEditStatus.SUCCESS,
          runtimeMs: Math.round((typeof performance !== "undefined" ? performance.now() : Date.now()) - startedAt)
        });
        return { ok: true };
      } catch (error) {
        // Revert the optimistic patch to the row's previous values.
        const revertPatch = Object.keys(plan.patch).reduce((acc, key) => {
          acc[key] = record[key];
          return acc;
        }, {});
        dispatch(
          actionCreator(view, isPatientFanOut ? [{ patientId: record.patientId, ...revertPatch }] : [{ ...record }])
        );
        if (record.patientId) {
          dispatch(CommonActions.updateRecentPatient(record.patientId, revertPatch));
        }
        ErrorMessage(t("inline_edit.error_toast"));
        trackInlineEditCompleted({
          attribute: columnKey,
          status: InlineEditStatus.FAILURE,
          runtimeMs: Math.round((typeof performance !== "undefined" ? performance.now() : Date.now()) - startedAt)
        });
        return { ok: false, error };
      }
    },
    [dispatch, t, filtersModified, trackInlineEditTriggered, trackInlineEditCompleted, lookups, view, actionCreator]
  );

  return { commit };
};

export default useInlineEditPatient;
